'use client';
import { Suspense, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { HeaderNavProps } from './types';
import { Logo, Profile, Search } from 'mmds';
import HeaderNavItems from '@app/components/header-nav/header-nav-items';
import Linker from '@app/components/linker/linker';
import Toolbar from '@app/components/ui/toolbar';
import getCurrencySymbol from '@app/lib/helpers/get-currency-symbol';
import useLoopReturns from '@app/lib/loop-returns/state';
import {
    addScrollDirectionListener,
    removeScrollDirectionListener,
} from '@app/lib/observers/throttled-scroll-direction-listener';
import CartIndicator from '@app/components/cart-indicator/cart-indicator';
import { Language } from '@app/lib/store-context/Context';
import { getContext } from '@app/lib/store-context/get-context';
import cn from 'clsx';
import s from './header-nav.module.css';

const HeaderNavDesktop = ({
    items,
    activeItem,
    callbacks,
    lang,
}: HeaderNavProps) => {
    const pathname = usePathname();
    const { isCreditShoppingActive } = useLoopReturns((state) => ({
        isCreditShoppingActive: state.isCreditShoppingActive,
    }));
    // The SRC value seems to depend on the environment. In the storybook environment, it's a string. In the web environment, it's an object.
    const [scrollDirection, setScrollDirection] = useState<string>('down');
    const isProductsPage = /\/(n)?products\//.test(pathname);
    const context = getContext(lang as Language);

    useEffect(() => {
        if ((window as any) === 'undefined') return;
        addScrollDirectionListener(window, setScrollDirection, 50);
        return () =>
            removeScrollDirectionListener(window, setScrollDirection, 50);
    }, []);

    return (
        <Suspense fallback={<div>Loading navigation...</div>}>
            <Toolbar
                shouldStick={scrollDirection === 'up'}
                position="top"
                variant="headerNav"
            >
                <div
                    className={cn(
                        s.root,
                        s.desktop,
                        'mmds-copy-three',
                        isProductsPage ? s.products : ''
                    )}
                >
                    <HeaderNavItems
                        items={items}
                        activeItem={activeItem}
                        callbacks={callbacks}
                        lang={lang as Language}
                    />
                    <div className={s.logo} data-testid={`dn-logo`}>
                        <Linker
                            href={'/'}
                            title={'MAAP Cycling Apparel Home'}
                            lang={lang as Language}
                        >
                            <Logo />
                        </Linker>
                    </div>
                    <div className={s.support}>
                        {!isCreditShoppingActive && (
                            <button
                                className={cn(s.supportItem, s.end)}
                                onClick={callbacks.toggleLanguageSwitch}
                            >
                                {context.countryCode} / {context.currencyCode}{' '}
                                {getCurrencySymbol(
                                    lang as Language,
                                    context.currencyCode
                                )}
                            </button>
                        )}
                        <button
                            className={cn(s.supportItem, s.end)}
                            onClick={callbacks.toggleSupport}
                        >
                            Support
                        </button>
                        <div className={cn(s.supportIcons, s.end)}>
                            <button
                                className={cn(s.icon, s.end)}
                                onClick={callbacks.toggleSearch}
                            >
                                <Search />
                            </button>
                            <button
                                className={cn(s.icon, s.end)}
                                onClick={callbacks.toggleProfile}
                            >
                                <Profile />
                            </button>
                            <button
                                className={cn(s.icon, s.end)}
                                onClick={callbacks.toggleCart}
                            >
                                <CartIndicator />
                            </button>
                        </div>
                    </div>
                </div>
            </Toolbar>
        </Suspense>
    );
};

export default HeaderNavDesktop;
